import { useState } from "react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { MessageSquare, Send } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-ZA", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
    });
  } catch { return dateStr; }
}

export default function NotesPanel({ jobId, notes = [], onNoteAdded }) {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const addNote = async () => {
    if (!message.trim()) return;
    setLoading(true);
    try {
      await api.post(`/job-cards/${jobId}/notes`, { message: message.trim() });
      setMessage("");
      toast.success("Note added");
      if (onNoteAdded) onNoteAdded();
    } catch {
      toast.error("Failed to add note");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="notes-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare size={16} className="text-teal-600" />
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Notes</h3>
      </div>

      <div className="flex gap-2">
        <Textarea
          data-testid="note-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a note..."
          className="bg-white border-slate-200 text-sm min-h-[60px] resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNote(); }
          }}
        />
        <Button
          data-testid="add-note-btn"
          onClick={addNote}
          disabled={loading || !message.trim()}
          size="icon"
          className="bg-teal-600 hover:bg-teal-700 text-white shrink-0 self-end"
        >
          <Send size={14} />
        </Button>
      </div>

      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {notes.length === 0 && (
          <p className="text-slate-400 text-xs italic">No notes yet</p>
        )}
        {notes.map((note) => (
          <div
            key={note.id}
            data-testid="note-item"
            className="bg-slate-100 border border-slate-200 rounded-xl p-3"
          >
            <p className="text-sm text-slate-800">{note.message}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[10px] text-teal-600 font-medium">{note.created_by_name}</span>
              <span className="text-[10px] text-slate-400">{formatDate(note.timestamp)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
